import { cast, flow, getParentOfType, getRoot, Instance, types } from 'mobx-state-tree';
import { CmsApi, CoreApi } from 'service/axios-api';
import { replaceAll } from 'service/utils';
import { LocDateFromEpoch, LocField } from 'store/localizations';
import { IRootStore } from 'store/root';
import { NotificationCenterEventsSession, NotificationCenterSession } from './session';

const NotificationCenterItemCdn = types.model('Notification Center CDN Item', {
    eventType: types.string,
    message: LocField,
    image: LocField,
    link: LocField,
});

export interface INotificationCenterItem extends Instance<typeof NotificationCenterItem> {}

const NotificationCenterItem = types
    .model('Notification Center Item', {
        notificationId: types.number,
        targetType: types.string, // product, user
        productId: types.string,
        umbracoKey: types.string,
        beginAt: LocDateFromEpoch,
        endAt: types.number,
        cdn: types.maybe(NotificationCenterItemCdn),
        state: types.optional(types.string, 'init'),
        hasRead: types.optional(types.boolean, false),
        customType: types.optional(types.string, ''),
        custom: types.maybeNull(types.string),
        isRealtime: types.optional(types.boolean, false),
    })
    .views((self) => {
        const views = {
            get parent(): INotificationCenter {
                return getParentOfType(self, NotificationCenter);
            },
            get root(): IRootStore {
                return getRoot<IRootStore>(self);
            },
            get removed(): boolean {
                return views.parent.session.removedIDs.includes(self.notificationId);
            },
            get unread(): boolean {
                return !views.parent.session.readIDs.includes(self.notificationId);
            },
            get expired(): boolean {
                return views.root.systemInfo.serverTime !== 0 && views.root.systemInfo.serverTime >= self.endAt;
            },
            get product() {
                return views.root.appStore.pc.find((x) => x.productId === self.productId);
            },
        };
        return views;
    })
    .actions((self) => {
        const actions = {
            afterCreate() {
                actions.fetch();
            },
            fetch: flow(function* () {
                if (self.state === 'loading' || !self.umbracoKey) return;
                self.state = 'loading';
                let customData = null;
                if (self.custom) {
                    try {
                        customData = JSON.parse(self.custom);
                    } catch (e) {
                        console.error(e);
                    }
                }
                try {
                    const data = yield CmsApi.get(`/frontend/global-notification/${self.umbracoKey}.json`);
                    if (customData) {
                        customData = { ...data, ...customData };
                        if (customData.parameters) {
                            const mapObj = customData.parameters.reduce(
                                (previousValue: object, value: string, index: number) => ({ ...previousValue, [`{${index}}`]: value }),
                                {}
                            );
                            for (let locale in customData.message) {
                                customData.message[locale] = replaceAll(customData.message[locale], mapObj);
                            }
                        }
                        // exception for filtering customized message if there is still remained string interpolation parameters like {0}, {1} - requested by Jisung and Choonwhae
                        for (let locale in customData.message) {
                            if (customData.message[locale].match(/\{[\d]+\}/g)) {
                                console.error('blocked notification message due to remained string interpolation parameter like {0}', self);
                                actions.remove();
                                return;
                            }
                        }
                    }
                    self.cdn = cast(customData ? customData : data);
                    self.state = 'done';

                    if (self.isRealtime && customData && self.product) {
                        window.ipcRenderer.invoke(
                            'showTrayNotification',
                            self.product?.name,
                            self.cdn?.message.value,
                            self.root.regionConfig.cdnHost + self.product?.navigationGameIcon,
                            self.productId
                        );
                        self.isRealtime = false;
                    }
                } catch (e) {
                    console.error(e);
                    self.state = 'error';
                }
            }),
            read() {
                self.hasRead = true;
            },
            readImmediately() {
                if (self.unread) self.parent.session.read(self.notificationId);
            },
            update(endAt: number) {
                self.endAt = endAt;
                actions.fetch();
            },
            remove() {
                if (self.targetType === 'USER') {
                    CoreApi.delete(`/notification-center/v1/notifications/${self.notificationId}`);
                }
                self.parent.session.remove(self.notificationId);
            },
        };
        return actions;
    });

export interface INotificationCenter extends Instance<typeof NotificationCenter> {}

const NotificationCenter = types
    .model('Notification Center Store', {
        data: types.array(NotificationCenterItem), // it should have all api results
        show: types.optional(types.boolean, false),
        arrowPos: types.optional(types.number, 0), // top arrow in notification's balloon box
        session: types.optional(NotificationCenterSession, {}),
        eventSession: types.optional(NotificationCenterEventsSession, {}),
    })
    .views((self) => {
        const views = {
            get root(): IRootStore {
                return getRoot<IRootStore>(self);
            },
            get list() {
                const ownedGames = ['10000', ...views.root.appStore.library.map((x) => x.productId)];
                return self.data
                    .filter(
                        (item) =>
                            !(item.removed || item.expired) &&
                            ownedGames.includes(item.productId) &&
                            item.cdn &&
                            self.eventSession.subscribedEvents.includes(item.cdn.eventType)
                    )
                    .sort((a, b) => b.beginAt.serverTime - a.beginAt.serverTime);
            },
            get unreadCount() {
                return views.list.filter((item) => item.unread).length;
            },
        };
        return views;
    })
    .actions((self) => ({
        fetch: flow(function* () {
            let {
                data: { notifications },
            } = yield CoreApi.get('/notification-center/v1/notifications');

            // api return value can be null
            if (!notifications) {
                notifications = [];
            } else {
                // filter by region (api doesn't support it, so it needs to be handled by client side)
                notifications = notifications.filter(
                    (v: any) => !v.membershipNo || v.membershipNo.toString() === self.root.regionConfig.cmsCode
                );
            }

            self.data = cast(notifications);
        }),
        removeAll() {
            CoreApi.delete('/notification-center/v1/notifications');
            self.session.removedIDs = cast(self.data.map((item) => item.notificationId));
        },
        add(data: any) {
            if (data.membershipNo && data.membershipNo.toString() !== self.root.regionConfig.cmsCode) return; // filter by region
            self.data.push(cast(data));
        },
        addCustomNotification({
            notificationId,
            targetType = 'PRODUCT',
            productId = '10000',
            beginAt = 1628716779,
            endAt = 1893456000,
            membershipNo = self.root.regionConfig.cmsCode,
            eventType,
            link = '',
            image = '',
            customType,
        }: {
            notificationId: number;
            targetType?: string;
            productId?: string;
            beginAt?: number;
            endAt?: number;
            membershipNo?: string;
            eventType: string;
            link?: string;
            image?: string;
            customType: string;
        }) {
            self.data.push(
                cast({
                    notificationId,
                    targetType,
                    productId,
                    umbracoKey: '',
                    beginAt,
                    endAt,
                    membershipNo,
                    cdn: {
                        eventType,
                        image: {
                            'en-US': image,
                        },
                        link: {
                            'en-US': link,
                        },
                        productId,
                    },
                    customType,
                } as unknown as INotificationCenterItem)
            );
        },
        toggleShow() {
            self.show = !self.show;
            if (!self.show) {
                self.list.forEach((item) => {
                    if (item.hasRead) self.session.read(item.notificationId);
                    item.hasRead = false;
                });
            }
        },
        setArrowPos(val: number) {
            self.arrowPos = val;
        },
    }));

export default NotificationCenter;
